import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import type { IBrazilianRealEstateFundTopLevelFragment } from "lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import type { ICompanyIssuerFieldsFragment } from "lib/graphql/fragments/__generated__/CompanyIssuerFields.generated";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { uniqBy } from "lodash";
import { IPlatformVertical, ISecurityCountry } from "types/graphqlTypes";

export function deduplicateSelectedIssuersList<Issuer extends Omit<ICompanyIssuerFieldsFragment, "__typename">>(
  issuers: Issuer[],
) {
  return uniqBy(issuers, (issuer) => issuer._id);
}

export type UrlSafeIssuerRef = {
  tickerSymbol: string;
  exchangeId?: string | null;
  country?: ISecurityCountry | null;
  vertical?: IPlatformVertical | null;
};

export function issuerRefToGraphQLInput(ref: UrlSafeIssuerRef) {
  return {
    vertical: ref.vertical || null,
    exchangeId: ref.exchangeId || null,
    country: ref.country || null,
    tickerSymbol: ref.tickerSymbol,
  };
}

export function encodeIssuerRefsToUrl(refs: UrlSafeIssuerRef[]) {
  return refs
    .map(({ vertical, country, exchangeId, tickerSymbol }) => {
      // Very specific
      if (vertical) {
        return `${vertical}:${tickerSymbol}`.toLowerCase();
      }
      // A little bit more specific
      if (exchangeId) {
        return `${exchangeId}:${tickerSymbol}`.toLowerCase();
      }
      // Country is the least specific
      if (country) {
        return `${country}:${tickerSymbol}`.toLowerCase();
      }

      return tickerSymbol;
    })
    .join(",");
}

export function encodeIssuerRefToUrl(ref: UrlSafeIssuerRef) {
  const encoded = encodeIssuerRefsToUrl([ref]);
  return encoded || null;
}

export function decodeIssuerRefFromUrl(input: string): UrlSafeIssuerRef | null {
  const refs = decodeIssuerRefsFromUrl(input);
  return refs.length === 1 ? refs[0] : null;
}

export function decodeIssuerRefsFromUrl(input: string): UrlSafeIssuerRef[] {
  const segments = input
    .split(",")
    .map((segment) => segment.trim())
    .filter((segment) => segment !== "");

  return segments
    .map((segment) => {
      const pattern =
        /^(?:(?:(?<exchangeId>(?:nys|nas|bvmf)|(?<country>(?:us|br))|(?<vertical>(?:acoes|fiis|stocks|reits))):)?(?<tickerSymbol>[a-z0-9]{1,6}))$/g;
      const output = pattern.exec(segment);
      if (!output) {
        return null;
      }
      const { groups } = output;
      if (!groups?.tickerSymbol) {
        return null;
      }
      if (
        groups.vertical &&
        [IPlatformVertical.Acoes, IPlatformVertical.Fiis, IPlatformVertical.Stocks, IPlatformVertical.Reits].includes(
          groups.vertical.toUpperCase() as IPlatformVertical,
        )
      ) {
        return {
          vertical: groups.vertical.toUpperCase() as IPlatformVertical,
          tickerSymbol: groups.tickerSymbol.toUpperCase(),
          exchangeId: null,
          country: null,
        };
      }
      if (groups.exchangeId && ["NYS", "NAS", "BVMF"].includes(groups.exchangeId.toUpperCase())) {
        return {
          vertical: null,
          tickerSymbol: groups.tickerSymbol.toUpperCase(),
          exchangeId: groups.exchangeId.toUpperCase(),
          country: null,
        };
      }
      if (
        groups.country &&
        [ISecurityCountry.Us, ISecurityCountry.Br].includes(groups.country.toUpperCase() as ISecurityCountry)
      ) {
        return {
          vertical: null,
          tickerSymbol: groups.tickerSymbol.toUpperCase(),
          exchangeId: null,
          country: groups.country.toUpperCase() as ISecurityCountry,
        };
      }
      return {
        vertical: null,
        tickerSymbol: groups.tickerSymbol.toUpperCase(),
        exchangeId: null,
        country: null,
      };
    })
    .filter(notUndefinedOrNull);
}

export function getBusinessDescription(
  issuer:
    | IBrazilianCompanyIssuerTopLevelFragment
    | IBrazilianRealEstateFundTopLevelFragment
    | IAmericanCompanyIssuerTopLevelFragment,
) {
  if (issuer.__typename === "AmericanCompanyIssuer") {
    return issuer.shortBusinessDescription;
  }

  if (issuer.__typename === "BrazilianCompanyIssuer") {
    return issuer.businessDescription;
  }

  return issuer.shortDescription;
}
